import React, { useState, useEffect, useCallback, useRef } from "react";
import { useSelector } from "react-redux";
import {
  MdKeyboardArrowDown,
  MdImage,
  MdPerson,
  MdBlock,
  MdAutoAwesome,
  MdError,
  MdCheckCircle,
  MdInfo,
  MdArrowBack,
  MdPendingActions,
} from "react-icons/md";
import styles from "./ImageProcess.module.css";
import { useImageProcess } from "../../customHook/ImageProcess/useImageProcess";
import ZoomModal from "../../components/ZoomModal/ZoomModal";
import ZoomButton from "../../components/buttons/ZoomButton";
import { endpoint } from "../../api/endpoint";
import { apiHandler } from "../../api";

// Icon imports
import watchSvg from "../../assets/image/watch.svg";
import braceletSvg from "../../assets/image/bracelet.svg";
import pendantSvg from "../../assets/image/pendant.svg";
import braidSvg from "../../assets/image/braid.svg";
import curlySvg from "../../assets/image/curly.svg";
import randomSvg from "../../assets/image/random.svg";
import leaningSvg from "../../assets/image/leaning.svg";
import standingSvg from "../../assets/image/standing.svg";
import sittingSvg from "../../assets/image/sitting.svg";
import casualSvg from "../../assets/image/casual.svg";
import movingSvg from "../../assets/image/moving.svg";
import interactiveSvg from "../../assets/image/interactive.svg";
import straightSvg from "../../assets/image/straight.svg";
import wavySvg from "../../assets/image/wavy.svg";
import heelsSvg from "../../assets/image/heels.svg";
import shoesSvg from "../../assets/image/shoes.svg";
import sandalsSvg from "../../assets/image/sandals.svg";
import slippersSvg from "../../assets/image/slippers.svg";
import frontSvg from "../../assets/image/front.svg";
import backSvg from "../../assets/image/back.svg";
import sideSvg from "../../assets/image/side.svg";
import noneMakeupSvg from "../../assets/image/none-makeup.svg";
import minimalMakeupSvg from "../../assets/image/minimal-makeup.svg";
import moderateMakeupSvg from "../../assets/image/moderate-makeup.svg";
import heavyMakeupSvg from "../../assets/image/heavy-makeup.svg";
import OneOneSvg from "../../assets/image/1_1_empty.svg";
import FourFiveSvg from "../../assets/image/4_5_empty.svg";
import FiveFourSvg from "../../assets/image/5_4_empty.svg";
import ThreeFourSvg from "../../assets/image/3_4_empty.svg";
import FourThreeSvg from "../../assets/image/4_3_empty.svg";
import TwoThreeSvg from "../../assets/image/2_3_empty.svg";
import ThreeTwoSvg from "../../assets/image/3_2_empty.svg";
import SixteenNineSvg from "../../assets/image/16_9_empty.svg";
import NineSixteenSvg from "../../assets/image/9_16_empty.svg";

// Custom select dropdown with icons and consistent height/layout
const IconSelect = ({
  label,
  value,
  options = [],
  onChange,
  getIconSvg,
  openDropdownKey,
  toggleDropdown,
  closeDropdown,
  fieldKey,
  isCustom,
  onCustomClick,
}) => {
  const isOpen = openDropdownKey === fieldKey;
  const selectedIcon = getIconSvg ? getIconSvg(value) : null;

  return (
    <div
      className={`${styles.settingField} flex flex-col gap-1 text-left w-full relative`}
      style={{ zIndex: isOpen ? 90 : 10 }}
    >
      <span className="text-[10px] font-semibold text-secondary">{label}</span>

      {!isCustom ? (
        <div className="relative w-full">
          {/* Dropdown selected box */}
          <div
            onClick={(e) => {
              e.stopPropagation();
              toggleDropdown(fieldKey);
            }}
            className={`${styles.settingSelect} flex items-center justify-between border border-outline rounded-md bg-surface-container-lowest px-2.5 py-1.5 focus:border-primary cursor-pointer w-full text-[11px] text-primary select-none h-[32px]`}
          >
            <div className="flex items-center gap-2 truncate">
              {selectedIcon && (
                <img
                  src={selectedIcon}
                  alt=""
                  className="w-4 h-4 object-contain shrink-0"
                />
              )}
              <span className="truncate">{value || "Default"}</span>
            </div>
            <MdKeyboardArrowDown className="text-[16px] text-secondary shrink-0 select-none" />
          </div>

          {/* Options list popup */}
          {isOpen && (
            <div
              className={`absolute left-0 right-0 mt-1 bg-surface-container-lowest border border-outline rounded-md shadow-lg z-[80] max-h-[160px] overflow-y-auto`}
            >
              {(options || []).map((opt) => {
                const isActive = value === opt;
                const icon = getIconSvg ? getIconSvg(opt) : null;
                return (
                  <div
                    key={opt || "__default__"}
                    onClick={(e) => {
                      e.stopPropagation();
                      onChange(opt);
                      closeDropdown();
                    }}
                    className={`flex items-center gap-2 px-2.5 py-2 text-[11px] cursor-pointer hover:bg-surface-container transition-colors select-none ${
                      isActive
                        ? "bg-surface-container font-semibold text-primary"
                        : "text-secondary hover:text-primary"
                    }`}
                  >
                    {icon && (
                      <img
                        src={icon}
                        alt=""
                        className="w-4 h-4 object-contain shrink-0"
                      />
                    )}
                    <span className="truncate">{opt || "Default"}</span>
                  </div>
                );
              })}
              {onCustomClick && (
                <div
                  onClick={(e) => {
                    e.stopPropagation();
                    onCustomClick();
                    closeDropdown();
                  }}
                  className="flex items-center px-2.5 py-2 text-[11px] text-primary font-semibold hover:bg-surface-container cursor-pointer border-t border-outline/40 select-none"
                >
                  + Custom
                </div>
              )}
            </div>
          )}
        </div>
      ) : (
        <div className="flex gap-2 items-center w-full">
          <input
            type="text"
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder={`Enter custom ${label.toLowerCase()}`}
            className={`${styles.settingInput} text-[11px] border border-outline rounded-md bg-surface-container-lowest px-2.5 py-1.5 focus:border-primary focus:ring-primary w-full text-primary h-[32px]`}
          />
          {onCustomClick && (
            <button
              type="button"
              className="text-[10px] font-semibold text-secondary hover:text-primary underline border-none bg-transparent cursor-pointer shrink-0"
              onClick={onCustomClick}
            >
              Back
            </button>
          )}
        </div>
      )}
    </div>
  );
};

const ImageProcess = ({
  selectedProduct,
  selectedModel,
  selectedBackgroundImage,
  setStep,
  step,
  generatedImage,
  setGeneratedImage,
  showMainProduct,
  setShowMainProduct,
  lastGeneratedParams,
  setLastGeneratedParams,
  selectionMode = "model",
}) => {
  const authToken = useSelector((state) => state.auth.token);
  const gender = selectedProduct?.category === "men" ? "men" : "women";
  const modelType = selectedProduct?.modelType;

  const [generationOptionsConfig, setGenerationOptionsConfig] = useState({});
  const [generationOptions, setGenerationOptions] = useState({});
  const [customFields, setCustomFields] = useState({});
  const [activePreviewIndex, setActivePreviewIndex] = useState(0);
  const [openDropdownKey, setOpenDropdownKey] = useState(null);

  const generationParamsRef = useRef(null);

  // Reset active preview when selections change
  useEffect(() => {
    setActivePreviewIndex(0);
  }, [selectedProduct, selectedModel, selectedBackgroundImage]);

  // Close dropdown on click outside
  useEffect(() => {
    const handleDocumentClick = (event) => {
      if (
        generationParamsRef.current &&
        !generationParamsRef.current.contains(event.target)
      ) {
        setOpenDropdownKey(null);
      }
    };
    document.addEventListener("mousedown", handleDocumentClick);
    return () => {
      document.removeEventListener("mousedown", handleDocumentClick);
    };
  }, []);

  // Fetch generation options from API (only for model mode)
  useEffect(() => {
    if (selectionMode !== "model" || !authToken || !selectedProduct) return;

    const fetchGenerationOptions = async () => {
      try {
        const response = await apiHandler({
          url: `${endpoint.API_BASE_URL}${endpoint.GET_GENERATION_OPTIONS}?gender=${gender}`,
          method: "GET",
          authToken: { token: authToken },
          noCache: true,
        });

        if (response?.data?.data) {
          const config = response.data.data;
          setGenerationOptionsConfig(config);

          // Initialize generationOptions from config
          const initialOptions = {};
          Object.entries(config).forEach(([key, fieldConfig]) => {
            if (
              fieldConfig &&
              fieldConfig.type === "select" &&
              Array.isArray(fieldConfig.options) &&
              fieldConfig.options.length > 0
            ) {
              initialOptions[key] = "";
            } else {
              initialOptions[key] = fieldConfig.default || "";
            }
          });
          setGenerationOptions(initialOptions);
        } else {
          setGenerationOptionsConfig({});
          setGenerationOptions({});
        }
      } catch (err) {
        console.error("Error fetching generation options:", err);
        setGenerationOptionsConfig({});
        setGenerationOptions({});
      }
    };

    fetchGenerationOptions();
  }, [gender, modelType, selectionMode, authToken, selectedProduct]);

  const [modal, setModal] = useState({
    front: "",
    back: "",
    showFlip: true,
    isBackground: false,
  });

  const openModal = (front, back, showFlip = true, isBackground = false) => {
    setModal({ front, back, showFlip, isBackground });
  };

  const closeModal = () => {
    setModal({ front: "", back: "" });
  };

  const handleOptionChange = (key, value) => {
    const normalizedValue =
      String(value || "").toLowerCase() === "default" ? "" : value;
    setGenerationOptions((prev) => ({ ...prev, [key]: normalizedValue }));
  };

  const toggleDropdown = (key) => {
    setOpenDropdownKey((currentKey) => (currentKey === key ? null : key));
  };

  const closeDropdown = () => {
    setOpenDropdownKey(null);
  };

  const buildDropdownOptions = useCallback((options = []) => {
    const validOptions = Array.isArray(options)
      ? options.filter(
          (opt) =>
            opt !== undefined && opt !== null && String(opt).trim() !== "",
        )
      : [];

    const withoutDefaultLiteral = validOptions.filter(
      (opt) => String(opt).toLowerCase() !== "default",
    );

    return ["", ...withoutDefaultLiteral];
  }, []);

  // SVG Icon mappings
  const getAccessorySvg = (value) => {
    if (!value) return null;
    const lowerValue = value.toLowerCase();
    if (lowerValue.includes("watch") || lowerValue.includes("wrist"))
      return watchSvg;
    if (lowerValue.includes("bracelet")) return braceletSvg;
    if (lowerValue.includes("pendant") || lowerValue.includes("necklace"))
      return pendantSvg;
    return null;
  };

  const getHairstyleSvg = (value) => {
    if (!value) return null;
    const lowerValue = value.toLowerCase();
    if (lowerValue.includes("braid")) return braidSvg;
    if (lowerValue.includes("curly")) return curlySvg;
    if (lowerValue.includes("straight")) return straightSvg;
    if (lowerValue.includes("wavy")) return wavySvg;
    return null;
  };

  const getFootwearSvg = (value) => {
    if (!value) return null;
    const lowerValue = value.toLowerCase();
    if (lowerValue.includes("heels")) return heelsSvg;
    if (lowerValue.includes("shoes")) return shoesSvg;
    if (lowerValue.includes("sandals")) return sandalsSvg;
    if (lowerValue.includes("slippers")) return slippersSvg;
    return null;
  };

  const getViewSvg = (value) => {
    if (!value) return null;
    const lowerValue = value.toLowerCase();
    if (lowerValue.includes("front")) return frontSvg;
    if (lowerValue.includes("side")) return sideSvg;
    if (lowerValue.includes("back")) return backSvg;
    return null;
  };

  const getMakeupSvg = (value) => {
    if (!value) return null;
    const lowerValue = value.toLowerCase();
    if (lowerValue.includes("none")) return noneMakeupSvg;
    if (lowerValue.includes("minimal")) return minimalMakeupSvg;
    if (lowerValue.includes("moderate")) return moderateMakeupSvg;
    if (lowerValue.includes("heavy")) return heavyMakeupSvg;
    return null;
  };

  const getAspectRatioSvg = (value) => {
    if (!value) return null;
    switch (value) {
      case "1:1":
        return OneOneSvg;
      case "2:3":
        return TwoThreeSvg;
      case "3:2":
        return ThreeTwoSvg;
      case "4:5":
        return FourFiveSvg;
      case "5:4":
        return FiveFourSvg;
      case "3:4":
        return ThreeFourSvg;
      case "4:3":
        return FourThreeSvg;
      case "16:9":
        return SixteenNineSvg;
      case "9:16":
        return NineSixteenSvg;
      default:
        return null;
    }
  };

  const getPoseSvg = (value) => {
    if (!value) return null;
    const lowerValue = value.toLowerCase();
    if (lowerValue.includes("random")) return randomSvg;
    if (lowerValue.includes("leaning")) return leaningSvg;
    if (lowerValue.includes("standing")) return standingSvg;
    if (lowerValue.includes("sitting")) return sittingSvg;
    if (lowerValue.includes("casual")) return casualSvg;
    if (lowerValue.includes("moving")) return movingSvg;
    if (lowerValue.includes("interactive")) return interactiveSvg;
    return null;
  };

  const getIconSvg = (fieldKey, val) => {
    if (fieldKey === "accessory") return getAccessorySvg(val);
    if (fieldKey === "hairstyle") return getHairstyleSvg(val);
    if (fieldKey === "footwear") return getFootwearSvg(val);
    if (fieldKey === "view") return getViewSvg(val);
    if (fieldKey === "makeup") return getMakeupSvg(val);
    if (fieldKey === "aspect_ratio") return getAspectRatioSvg(val);
    if (fieldKey === "pose") return getPoseSvg(val);
    return null;
  };

  const aspectRatioOptions = [
    "1:1",
    "2:3",
    "3:2",
    "4:5",
    "5:4",
    "3:4",
    "4:3",
    "16:9",
    "9:16",
  ];

  const { isValid, isLoading, error, handleProceed, existingQueueImages } =
    useImageProcess(
      selectedModel,
      selectedBackgroundImage,
      selectedProduct,
      selectedProduct?.modelType,
      gender,
      selectionMode,
      generationOptions,
      generatedImage,
      setGeneratedImage,
      showMainProduct,
      setShowMainProduct,
    );

  const onHandleProceed = async (e) => {
    e.preventDefault();
    if (!isValid || isLoading) return;
    setLastGeneratedParams({
      generationOptions: { ...generationOptions },
      modelName: selectedModel?.name,
      backgroundName: selectedBackgroundImage?.name,
      productTitle: selectedProduct?.title,
    });
    await handleProceed(e);
  };

  const showSettings =
    selectionMode === "model" &&
    Object.keys(generationOptionsConfig).length > 0;

  const productThumb = selectedProduct?.images?.[0] || selectedProduct?.image;
  const productImagesCount = selectedProduct?.images?.length || 0;

  const modelThumb =
    selectedModel?.images?.[0] || selectedModel?.image || selectedModel?.front;

  const hasExisting = existingQueueImages && existingQueueImages.length > 0;
  const activeImage = hasExisting
    ? existingQueueImages[activePreviewIndex]
    : null;
  const activeImageUrl = activeImage ? activeImage.url : "";
  const activeImageFullUrl = activeImage
    ? activeImage.fullUrl || activeImage.url
    : "";
  const activeImageLabel = activeImage
    ? activeImage.status || "Previous output found"
    : "Previous output found";

  const steps =
    selectionMode === "user"
      ? [
          { label: "Product", number: 1 },
          { label: "Model", number: 2 },
          { label: "Review", number: 3 },
        ]
      : [
          { label: "Product", number: 1 },
          { label: "Model", number: 2 },
          { label: "Background", number: 3 },
          { label: "Review", number: 4 },
        ];

  const renderOptionField = (fieldKey, fieldConfig) => {
    if (!fieldConfig) return null;
    const isCustom = !!customFields[fieldKey];
    const isSpecialCustomKey = ["accessory", "hairstyle", "footwear"].includes(
      fieldKey,
    );

    const label = fieldKey === "aspect_ratio" ? "Aspect" : fieldConfig.label;
    const optionsList = buildDropdownOptions(
      fieldKey === "aspect_ratio" ? aspectRatioOptions : fieldConfig.options,
    );

    return (
      <IconSelect
        key={fieldKey}
        label={label}
        value={generationOptions[fieldKey] || ""}
        options={optionsList}
        onChange={(val) => handleOptionChange(fieldKey, val)}
        getIconSvg={(val) => getIconSvg(fieldKey, val)}
        openDropdownKey={openDropdownKey}
        toggleDropdown={toggleDropdown}
        closeDropdown={closeDropdown}
        fieldKey={fieldKey}
        isCustom={isCustom}
        onCustomClick={
          isSpecialCustomKey
            ? () => {
                const toggled = !isCustom;
                setCustomFields((p) => ({ ...p, [fieldKey]: toggled }));
                handleOptionChange(fieldKey, "");
              }
            : null
        }
      />
    );
  };

  const renderSelectionsList = () => (
    <div className="flex flex-col gap-2">
      {/* Product Select Card */}
      <article className="border border-outline/70 rounded-lg p-3 flex items-center justify-between gap-3 bg-surface-container-lowest">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-14 h-16 rounded-md bg-surface-container border border-outline/60 overflow-hidden flex-shrink-0 flex items-center justify-center">
            {productThumb ? (
              <img
                className="w-full h-full object-contain mix-blend-multiply"
                alt="Product"
                src={productThumb}
              />
            ) : (
              <MdImage className="text-secondary/50" size={24} />
            )}
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-[10px] uppercase tracking-wider font-bold text-secondary">
                Product
              </span>
              <span className="px-2 py-0.5 rounded bg-surface-container text-[10px] font-semibold text-secondary">
                {selectedProduct?.modelType === "mannequin"
                  ? "Mannequin to Model"
                  : "Model to Model"}
              </span>
            </div>
            <h4 className="text-sm font-bold text-primary truncate">
              {selectedProduct?.title || "Product"}
            </h4>
            <p className="text-[10px] text-secondary mt-1">
              {productImagesCount} source image
              {productImagesCount > 1 ? "s" : ""}
            </p>
          </div>
        </div>
        <button
          onClick={() => setStep(1)}
          className="text-[10px] font-semibold text-secondary hover:text-primary underline underline-offset-2 border-none bg-transparent cursor-pointer"
        >
          Change
        </button>
      </article>

      {/* Model Select Card */}
      <article className="border border-outline/70 rounded-lg p-3 flex items-center justify-between gap-3 bg-surface-container-lowest">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-12 h-12 rounded-full bg-surface-container border border-outline/60 overflow-hidden flex-shrink-0 flex items-center justify-center">
            {modelThumb ? (
              <img
                className="w-full h-full object-cover object-top"
                alt="Model"
                src={modelThumb}
              />
            ) : (
              <MdPerson className="text-secondary/50" size={24} />
            )}
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-[10px] uppercase tracking-wider font-bold text-secondary">
                Model
              </span>
              <span className="px-2 py-0.5 rounded bg-surface-container text-[10px] font-semibold text-secondary">
                {selectedModel?.source === "custom"
                  ? "My Model"
                  : "Studio Model"}
              </span>
            </div>
            <h4 className="text-sm font-bold text-primary truncate">
              {selectedModel?.name || "Adrian"}
            </h4>
          </div>
        </div>
        <button
          onClick={() => setStep(2)}
          className="text-[10px] font-semibold text-secondary hover:text-primary underline underline-offset-2 border-none bg-transparent cursor-pointer"
        >
          Change
        </button>
      </article>

      {/* Background Select Card (Model selection mode only when a background is explicitly selected) */}
      {selectionMode !== "user" && selectedBackgroundImage && !selectedBackgroundImage.skipped && (
        <article className="border border-outline/70 rounded-lg p-3 flex items-center justify-between gap-3 bg-surface-container-lowest">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-12 h-12 rounded-md bg-surface-container border border-outline/60 overflow-hidden flex-shrink-0 flex items-center justify-center">
              {selectedBackgroundImage?.image ||
              selectedBackgroundImage?.front ? (
                <img
                  className="w-full h-full object-cover"
                  src={
                    selectedBackgroundImage?.image ||
                    selectedBackgroundImage?.front
                  }
                  alt="Background"
                />
              ) : selectedBackgroundImage?.skipped ? (
                <MdBlock className="text-secondary/60" size={24} />
              ) : (
                <MdAutoAwesome className="text-secondary/60" size={26} />
              )}
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-[10px] uppercase tracking-wider font-bold text-secondary">
                  Background
                </span>
                <span className="px-2 py-0.5 rounded bg-surface-container text-[10px] font-semibold text-secondary">
                  {selectedBackgroundImage?.skipped
                    ? "No Background"
                    : selectedBackgroundImage?.type === "auto"
                      ? "Auto Background"
                      : "Selected Background"}
                </span>
              </div>
              <h4 className="text-sm font-bold text-primary truncate">
                {selectedBackgroundImage?.skipped
                  ? "Skipped"
                  : selectedBackgroundImage?.name || "Auto Background"}
              </h4>
            </div>
          </div>
          <button
            onClick={() => setStep(3)}
            className="text-[10px] font-semibold text-secondary hover:text-primary underline underline-offset-2 border-none bg-transparent cursor-pointer"
          >
            Change
          </button>
        </article>
      )}
    </div>
  );

  return (
    <div
      className={`${styles.reviewPage} app-page flex-1 flex flex-col min-h-0 min-w-0 pb-20`}
    >
      {/* Title and Steps Progress indicator */}
      <div
        className={`${styles.reviewHeader} px-6 pt-4 pb-3 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4`}
      >
        <div>
          <h2 className="font-headline-md text-[20px] text-primary mb-0.5 font-bold tracking-tight">
            Review Generation
          </h2>
          <p className="font-body-md text-[10px] text-secondary">
            Review your selections and generation settings before submitting.
          </p>
        </div>
        <div
          className={`${styles.reviewStepper} app-glass-panel flex items-center px-4 py-2 self-start lg:self-auto`}
        >
          <div className={`${styles.reviewStepperTrack} flex items-center gap-4 text-xs font-medium`}>
            {steps.map((s, idx) => {
              const isCurrent = s.number === step;
              return (
                <React.Fragment key={s.label}>
                  {idx > 0 && (
                    <div className="w-6 h-0.5 bg-outline-variant"></div>
                  )}
                  <div className="flex items-center gap-2 text-primary">
                    <span
                      className={`w-5 h-5 rounded-full bg-primary text-on-primary flex items-center justify-center text-[10px] font-bold ${isCurrent ? "ring-4 ring-primary/10" : ""}`}
                    >
                      {s.number}
                    </span>
                    <span className="font-semibold">{s.label}</span>
                  </div>
                </React.Fragment>
              );
            })}
          </div>
        </div>
      </div>

      {/* Error alert */}
      {error && (
        <div className="app-error-state mx-6 mb-4 p-3 text-xs font-semibold rounded-md flex items-center gap-2">
          <MdError className="text-[16px]" />
          <span>{error}</span>
        </div>
      )}

      {/* Main Grid View */}
      <div
        className={`${styles.reviewScroll} flex-1 px-6 pt-1 pb-80 overflow-y-auto min-h-0`}
      >
        <div
          className={`${styles.reviewGrid} grid grid-cols-12 gap-4 items-start`}
        >
          {/* Selections / Output column */}
          <section
            className={`${styles.reviewSelectionsColumn} col-span-12 ${showSettings ? "lg:col-span-8" : ""} flex flex-col gap-3`}
          >
            <div
              className={`${styles.reviewCard} app-panel overflow-hidden`}
            >
              <div className="px-4 py-3 border-b border-outline/60">
                <h3 className="text-sm font-bold text-primary">
                  {hasExisting
                    ? "Your selections & Generated output"
                    : "Your selections"}
                </h3>
              </div>

              {hasExisting ? (
                <div
                  className={`${styles.reviewExistingGrid} grid grid-cols-12 gap-4 p-3 items-stretch`}
                >
                  {/* Left sub-column: Selections */}
                  <div className="col-span-12 md:col-span-7 flex flex-col gap-2">
                    {renderSelectionsList()}
                  </div>

                  {/* Right sub-column: Prominent Generated Output Preview */}
                  <div
                    className={`${styles.generatedPreviewCard} col-span-12 md:col-span-5 flex flex-col border border-outline/70 rounded-lg bg-surface overflow-hidden shadow-sm`}
                  >
                    {/* Header bar of preview */}
                    <div className="flex items-center justify-between border-b border-outline/50 px-3 py-2 bg-surface-container-lowest shrink-0">
                      <div>
                        <div className="flex items-center gap-1.5 text-green-700">
                          <MdCheckCircle className="text-[15px]" />
                          <span className="text-[10px] font-bold uppercase tracking-wider">
                            Existing generation
                          </span>
                        </div>
                        {activeImageLabel && (
                          <p className="text-[10px] text-secondary capitalize mt-0.5">
                            {activeImageLabel}
                          </p>
                        )}
                      </div>
                      {existingQueueImages.length > 1 && (
                        <span className="text-[9px] font-semibold text-secondary bg-surface-container px-1.5 py-0.5 rounded">
                          Image {activePreviewIndex + 1} of{" "}
                          {existingQueueImages.length}
                        </span>
                      )}
                    </div>

                    {/* Image display with hover magnification action */}
                    <div
                      data-bs-toggle="modal"
                      data-bs-target="#modelZoomModal"
                      onClick={() => openModal(activeImageFullUrl, "", false)}
                      className={`${styles.generatedPreviewImage} flex-1 min-h-[220px] md:min-h-[260px] bg-surface-container flex items-center justify-center overflow-hidden cursor-pointer relative group`}
                    >
                      <img
                        src={activeImageUrl}
                        alt="Generated Preview"
                        className="max-h-[260px] w-auto object-contain transition-transform duration-300 group-hover:scale-[1.02]"
                      />
                      {/* Consistent Bottom-Right Zoom Button */}
                      <ZoomButton
                        modalTarget="#modelZoomModal"
                        onClick={() =>
                          openModal && openModal(activeImageFullUrl, "", false)
                        }
                        positionClasses="absolute bottom-1.5 right-1.5"
                        size="md"
                        className="z-20"
                      />
                    </div>

                    {/* Thumbnail strip switcher */}
                    {existingQueueImages.length > 1 && (
                      <div
                        className={`${styles.generatedThumbStrip} border-t border-outline/50 p-2 flex gap-1.5 overflow-x-auto bg-surface-container-lowest justify-start shrink-0`}
                      >
                        {existingQueueImages.map((imgItem, idx) => {
                          const isActive = idx === activePreviewIndex;
                          return (
                            <button
                              key={imgItem.queueId || idx}
                              onClick={() => setActivePreviewIndex(idx)}
                              className={`w-11 h-15 shrink-0 rounded border overflow-hidden p-0 bg-surface-container-lowest transition-all cursor-pointer ${isActive ? "border-primary ring-2 ring-primary/5" : "border-outline hover:border-secondary/50"}`}
                              title={`View output #${idx + 1}`}
                            >
                              <img
                                className="w-full h-full object-cover object-top"
                                src={imgItem.url}
                                alt={`Thumbnail ${idx + 1}`}
                              />
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="p-3">{renderSelectionsList()}</div>
              )}
            </div>
          </section>

          {/* Generation Settings Sidebar column */}
          {showSettings && (
            <aside
              className={`${styles.settingsAside} col-span-12 lg:col-span-4`}
              ref={generationParamsRef}
            >
              <div
                className={`${styles.settingsPanel} app-glass-panel p-4 sticky top-24`}
              >
                <div className="mb-3 flex items-start justify-between gap-3">
                  <div>
                    <h3 className="font-headline-md text-[16px] font-bold text-primary">
                      Generation settings
                    </h3>
                    <p className="text-[11px] text-secondary mt-0.5">
                      Recommended defaults are applied automatically.
                    </p>
                  </div>
                  <span className={`${styles.defaultBadge} inline-flex shrink-0 items-center gap-1 rounded-md bg-sky-50 px-2 py-1 text-[10px] font-semibold text-sky-700 border border-sky-100`}>
                    <MdInfo className="text-[14px]" />
                    Default
                  </span>
                </div>

                {/* Option Fields Selection */}
                <div className={`${styles.settingsGrid} grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2 gap-2`}>
                  {Object.entries(generationOptionsConfig).map(
                    ([key, config]) => renderOptionField(key, config),
                  )}
                </div>
              </div>
            </aside>
          )}
        </div>
      </div>

      {/* Floating Action Footer */}
      <footer
        className={`${styles.actionFooter} app-footer-bar px-6 h-12 flex items-center`}
      >
        <div
          className={`${styles.actionFooterInner} flex justify-between items-center w-full`}
        >
          <button
            onClick={() => setStep(selectionMode === "user" ? 2 : 3)}
            className={`${styles.footerBackButton} inline-flex items-center gap-2 text-secondary hover:text-primary font-label-md text-xs font-semibold transition-colors border-none bg-transparent cursor-pointer`}
          >
            <MdArrowBack className="text-[18px]" />
            Back
          </button>

          <div
            className={`${styles.footerStatus} flex items-center gap-2 text-secondary`}
          >
            {hasExisting ? (
              <>
                <MdCheckCircle className="text-[18px] text-green-600" />
                <span className="font-body-md text-xs font-semibold">
                  Existing image found
                </span>
              </>
            ) : (
              <>
                <MdPendingActions className="text-[18px]" />
                <span className="font-body-md text-xs font-semibold">
                  Ready for queue
                </span>
              </>
            )}
          </div>

          <button
            onClick={onHandleProceed}
            disabled={!isValid || isLoading}
            className={`${styles.footerProceedButton} app-button-primary px-6 py-2 rounded-md font-semibold text-xs focus:outline-none inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer`}
          >
            {isLoading ? (
              <>
                Generating...
                <div className="animate-spin rounded-full h-4.5 w-4.5 border-2 border-on-primary border-t-transparent"></div>
              </>
            ) : (
              <>
                Proceed
                <MdAutoAwesome className="text-[16px]" />
              </>
            )}
          </button>
        </div>
      </footer>

      {/* Modal dialog popup zoom */}
      <ZoomModal
        modalId="modelZoomModal"
        front={modal.front}
        back={modal.back}
        onClose={closeModal}
        showFlip={modal.showFlip}
        isBackground={modal.isBackground}
      />
    </div>
  );
};

export default ImageProcess;
